/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { useCase } from "../../../provider/CaseProvider";
import PageContentContainer from "../../Container/PageContentContainer";
import PageHeadline from "../../PageHeadline/PageHeadline";
import React, { useState } from "react";
import LegalBirdPageLoading from "../../ContentLoader/LegalBirdPageLoading";
import { fetchCollection, queryKeys } from "../../../services/ReactQuery/reactQueryService";
import { useQuery } from "@tanstack/react-query";
import { Box, Stack } from "@mui/material";
import MediaObjectFolderView from "../../MediaObjectSection/MediaObjectFolderView";
import ButtonLoading from "../../Button/ButtonLoading";
import caseReferenceFileStyles from "../../Case/CaseReferenceFile/caseReferenceFileStyles";
import ContentBox from "../../ContentBox/ContentBox";
import { apiPost } from "../../../services/Api/apiCall";
import { useSnackbar } from "notistack";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { getCaseLink } from "../../../services/Product/ProductService/productService";

const CaseImportDocumentsFromPreviousInstancePage = () => {
  const { product, isLoading } = useCase();
  const { enqueueSnackbar } = useSnackbar();
  const currentUser = useCurrentUser();
  const [selectedMediaObjects, setSelectedMediaObjects] = useState<number[]>([]);

  let mediaObjectsFilterPreviousInstance = {
    product: product?.productClassName,
    productId: product?.linkedAdministrativeLawCaseIds["mainCaseId"],
  };

  const { data: mediaObjects } = useQuery({
    queryKey: queryKeys.collection("media_objects", mediaObjectsFilterPreviousInstance),
    queryFn: () => fetchCollection("media_objects", mediaObjectsFilterPreviousInstance),
    enabled: !!product
  });

  if (!product || isLoading) {
    return <LegalBirdPageLoading />;
  }

  if (product.productCategory !== "administrativeLaw" || product.administrativeCaseType === "main") {
    return null;
  }

  const handleChildInstanceImportDocuments = async () => {
    let snackbarMessage = "Dokument erfolgreich importiert";
    try {
      await apiPost("/lb/services/copy_media_object_to_administrative_law_child_case", {
        productId: product.id,
        mediaObjectIds: selectedMediaObjects,
        createdBy: currentUser["@id"],
      });
    } catch (e) {
      snackbarMessage = "Dokument konnte nicht importiert werden";
    }

    enqueueSnackbar(snackbarMessage, {
      variant: "custom",
      buttonType: "link",
      buttonLink: getCaseLink(product),
      buttonTarget: "_self",
      buttonText: "Zum Fall",
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "center",
      },
    });
  };

  return (
    <>
      <PageContentContainer>
        <PageHeadline main="Dokumente aus Hauptverfahren importieren" />
        <ContentBox>
          <Stack spacing={2} sx={{
            alignItems: "center"
          }}>
            <Box sx={{ width: "100%" }}>
              <MediaObjectFolderView
                mediaObjects={mediaObjects?.["hydra:member"]}
                selectableView
                selectedMediaObjects={selectedMediaObjects}
                setSelectedMediaObjects={setSelectedMediaObjects}
                isMediaObjectNonInteractive={true}
              />
            </Box>
            <ButtonLoading
              isLoading={isLoading}
              onClick={handleChildInstanceImportDocuments}
              variant={"outlined"}
              sx={caseReferenceFileStyles.button}
            >
              Kopieren
            </ButtonLoading>
          </Stack>
        </ContentBox>
      </PageContentContainer>
    </>
  );
};

export default CaseImportDocumentsFromPreviousInstancePage;
